import { ConnectionState as FluidConnectionState } from "fluid-framework";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import { useBrowserOnlineStatus } from "../../../hooks/use-browser-online-status.js";
import { recordErrorInSentry } from "../../../utils/init-sentry.js";
import { useAuth } from "../../auth/use-auth.js";
import type {
  FluidContainer,
  FluidServices,
  FluidSharedTree,
} from "../fluid.client.js";
import { addUserIfNotExist } from "../fluid.service.js";
import { useFluidContainer } from "./use-fluid-container.js";
import { useReconnectManager } from "./use-reconnect-manager.js";

type ConnectionState = "connecting" | "connected" | "disconnected";
const CONNECTION_REPORT_TIMEOUT_MS = 90_000;

export interface FluidConnectionManager {
  connectionState: ConnectionState;
  fluidUserId: string | null;
  container: FluidContainer | null;
  containerId: string | null;
  sharedTree: FluidSharedTree | null;
  services: FluidServices | null;
  error: Error | null;
  isRetrying: boolean;
  retry: () => void;
}

export function useFluidConnectionManager(): FluidConnectionManager {
  const { user } = useAuth();
  const [connectionState, setConnectionState] =
    useState<ConnectionState>("connecting");
  const [fluidUserId, setFluidUserId] = useState<string | null>(null);
  const {
    container,
    containerId,
    sharedTree,
    services,
    error,
    retry: retryContainer,
    isRetrying,
  } = useFluidContainer();

  const subscribeToFluidConnection = useCallback(
    (onStateChange: () => void) => {
      container?.on("connected", onStateChange);
      container?.on("disconnected", onStateChange);

      return () => {
        container?.off("connected", onStateChange);
        container?.off("disconnected", onStateChange);
      };
    },
    [container]
  );
  const getFluidConnectionState = useCallback(
    () => container?.connectionState,
    [container]
  );
  const fluidConnectionState = useSyncExternalStore(
    subscribeToFluidConnection,
    getFluidConnectionState,
    getFluidConnectionState
  );

  // Called when the container is connected
  const onConnected = useCallback(() => {
    setConnectionState("connected");
  }, []);

  // Called when all reconnection attempts have failed
  const onDisconnected = useCallback(async () => {
    setConnectionState("disconnected");
    await container?.disconnect();
  }, [container]);

  const { retryConnection } = useReconnectManager({
    container,
    onConnected,
    onDisconnected,
  });

  const isActuallyDisconnected = useCallback(() => {
    if (!container) {
      return true;
    }
    return container.connectionState !== FluidConnectionState.Connected;
  }, [container]);

  useBrowserOnlineStatus({
    onFocus: () => {
      if (isActuallyDisconnected()) {
        setConnectionState("connecting");
        retryConnection();
      }
    },
    onStatusChange: (status) => {
      if (status === "online" && isActuallyDisconnected()) {
        setConnectionState("connecting");
        retryConnection();
      }
    },
    onVisibilityChange: (visibility) => {
      if (visibility === "visible" && isActuallyDisconnected()) {
        setConnectionState("connecting");
        retryConnection();
      }
    },
  });

  // Register itself to watch over the container
  useEffect(() => {
    if (!container) {
      return;
    }

    let connectionReportTimeout: ReturnType<typeof setTimeout> | null = null;
    const clearConnectionReportTimeout = () => {
      if (connectionReportTimeout) {
        clearTimeout(connectionReportTimeout);
        connectionReportTimeout = null;
      }
    };
    const scheduleConnectionReport = () => {
      if (connectionReportTimeout) {
        return;
      }

      connectionReportTimeout = setTimeout(() => {
        connectionReportTimeout = null;
        if (container.connectionState === FluidConnectionState.Connected) {
          onConnected();
          return;
        }

        recordErrorInSentry(
          new Error("Fluid container did not reach connected state"),
          {
            extra: {
              connectionState: container.connectionState,
              disposed: container.disposed,
              fluidContainerId: containerId,
              online: navigator.onLine,
            },
            tags: {
              event: "ConnectionTimeout",
              feature: "fluid",
              fluidDocumentId: containerId ?? "unknown",
            },
          }
        );
      }, CONNECTION_REPORT_TIMEOUT_MS);
    };
    const handleConnected = () => {
      clearConnectionReportTimeout();
      onConnected();
    };
    const handleDisconnected = () => {
      setConnectionState("connecting");
      scheduleConnectionReport();
      retryConnection();
    };

    container.on("connected", handleConnected);
    container.on("disconnected", handleDisconnected);

    if (container.connectionState !== FluidConnectionState.Connected) {
      scheduleConnectionReport();
    }

    return () => {
      clearConnectionReportTimeout();
      container.off("connected", handleConnected);
      container.off("disconnected", handleDisconnected);
    };
  }, [container, containerId, onConnected, retryConnection]);

  // Track if this is the initial page load (vs. a reconnection)
  // On page load/reload: move user to entrance area
  // On reconnection: keep user at their current location
  const isInitialMount = useRef(true);

  useEffect(() => {
    if (!(sharedTree && user?.id)) {
      return;
    }

    const fluidUser = addUserIfNotExist({
      forceEntranceArea: isInitialMount.current,
      sharedTree,
      user,
    });
    if (fluidUser) {
      setFluidUserId(fluidUser.id);
    }

    isInitialMount.current = false;
  }, [sharedTree, user]);

  let currentConnectionState = connectionState;
  if (fluidConnectionState === FluidConnectionState.Connected) {
    currentConnectionState = "connected";
  } else if (connectionState === "connected") {
    currentConnectionState = "connecting";
  }

  return {
    connectionState: currentConnectionState,
    container,
    containerId,
    error,
    fluidUserId,
    isRetrying,
    retry: error ? retryContainer : () => retryConnection(true),
    services,
    sharedTree,
  };
}
